import React, { useEffect, useMemo, useState } from 'react';

import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPaymentSlipBatchApis } from '@apis/next/PaymentSlipBatchApis';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { FileArrayInput } from '@components/NextForms/FileArrayInput';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { moneyStrings } from '@data/money';
import { paymentSlipStrings } from '@data/paymentSlip';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { useFormUtils } from '@hooks/useFormUtils';
import { FileTypeHelper } from '@models/next/FileType';
import { PaymentSlipHelper } from '@models/next/PaymentSlip';
import {
  PaymentSlipBatch,
  PaymentSlipBatchForm,
  PaymentSlipBatchHelper,
  PaymentSlipBatchResponse,
} from '@models/next/PaymentSlipBatch';
import { decodeResultEqual, mapDecodeResult, unwrapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional } from '@utils/types';
import { observer } from 'mobx-react';
import * as r from 'remeda';

const strings = createStrings({
  ja: {
    title: () => ({
      create: `${paymentSlipBatchStrings.payment_slip_batch}を作成する`,
      edit: `${paymentSlipBatchStrings.payment_slip_batch}を編集する`,
    }),
  },
});
const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 100px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'scroll',
    maxHeight: 550,
  },
  messageContainer: {
    paddingTop: '75px',
    paddingBottom: '75px',
    textAlign: 'center',
    ...common.textGray700,
  },
  split: {
    gridColumn: 'span 3',
    backgroundColor: theme.palette.grey[300],
    height: '1px',
    margin: '2rem 0',
    ...common.flexCenter,
  },
  title: {
    backgroundColor: theme.palette.background.paper,
    padding: '0 0.5rem',
    ...common.textGray500,
  },
}));

interface PaymentSlipBatchFormDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'create' | 'edit';
  initialPaymentSlipBatch?: PaymentSlipBatch;
  onSuccess?: () => void;
}

export const PaymentSlipBatchFormDialog: React.FC<PaymentSlipBatchFormDialogProps> = observer(
  (props) => {
    const { open, onClose, type, initialPaymentSlipBatch, onSuccess } = props;

    const classes = { ...useStyles(), ...useCommonStyles() };

    const [step, setStep] = useState(0);

    const [response, setResponse] = useState<PaymentSlipBatchResponse>();

    const queryClient = useQueryClient();

    const payeesQuery = dashboardPaymentSlipApis.useGet(
      '/dashboard/payment-slip/payee',
      undefined,
      {
        staleTime: Infinity,
        enabled: open,
      },
    );

    const payees = payeesQuery.data ?? [];

    const postPaymentSlipBatch = dashboardPaymentSlipBatchApis.usePost(
      '/dashboard/payment-slip-batch',
      {},
      {
        onSuccess: (data) => {
          setResponse(data);
          onSuccess?.();
          queryClient.invalidateQueries(
            dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
          );
          queryClient.invalidateQueries(
            dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch'),
          );
        },
      },
    );

    const patchPaymentSlipBatch = dashboardPaymentSlipBatchApis.usePatch(
      '/dashboard/payment-slip-batch/:id',
      {
        params: { id: initialPaymentSlipBatch?.id ?? 0 },
      },
      {
        onSuccess() {
          onClose();
          queryClient.invalidateQueries(
            dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch/:id', {
              params: { id: initialPaymentSlipBatch?.id ?? 0 },
            }),
          );
        },
      },
    );

    const fileUpload = fileUploadApis.usePost('/file/upload');

    const helper = useMemo(
      () => mapOptional(initialPaymentSlipBatch, (d) => new PaymentSlipBatchHelper(d)),
      [initialPaymentSlipBatch],
    );

    const initialPaymentSlipBatchForm = useMemo(() => helper?.toForm(), [helper]);

    const form = useForm(PaymentSlipBatchForm, {
      initialOutput: initialPaymentSlipBatchForm,
      enableReinitialize: true,
    });

    const { fields } = form.root;

    useEffect(() => {
      if (form.isDirty) {
        const payee = payees.find((payee) =>
          decodeResultEqual(fields.payee.fields.id.decodeResult, payee.id),
        );
        if (payee) {
          fields.payee.setOutput(payee);
        }
      }
    }, [mapDecodeResult(fields.payee.fields.id.decodeResult, (d) => d, -1)]);

    const isOtherPayee = decodeResultEqual(fields.payee.fields.id.decodeResult, 0, false);

    const { renderAlertsAndSpinnerModal } = useFormUtils({
      mutations: [postPaymentSlipBatch, patchPaymentSlipBatch, fileUpload],
    });

    return (
      <>
        <ActionableDialog
          open={open}
          onClose={onClose}
          size="xl"
          source={import.meta.url}
          muiDialogProps={{ disablePortal: false }}
        >
          <ActionableDialogTitle textAlign="center">{strings.title()[type]}</ActionableDialogTitle>
          {step === 0 && (
            <form
              {...form.bindForm({
                onSubmit: async (values) => {
                  if (type === 'create') {
                    await postPaymentSlipBatch.mutateAsync({
                      ...values,
                      withdraw_certificate_files: [],
                      invoice_files: await FileTypeHelper.uploadLocalArrayToRemote(
                        values.invoice_files,
                        fileUpload.mutateAsync,
                      ),
                    });
                    setStep(1);
                  } else {
                    await patchPaymentSlipBatch.mutateAsync({
                      ...values,
                      withdraw_certificate_files: await FileTypeHelper.uploadLocalArrayToRemote(
                        values.withdraw_certificate_files,
                        fileUpload.mutateAsync,
                      ),
                      invoice_files: await FileTypeHelper.uploadLocalArrayToRemote(
                        values.invoice_files,
                        fileUpload.mutateAsync,
                      ),
                    });
                  }
                },
              })}
            >
              <div className={classes.dialogContent}>
                <IndexedFormContainer>
                  <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>

                  <IndexedLabel index={1} field={fields.batch_no} />
                  <label>{unwrapDecodeResult(fields.batch_no.decodeResult) || '-'}</label>

                  <label />
                  <label>{paymentSlipBatchStrings.payment_slip_batch}</label>
                  <div>
                    {initialPaymentSlipBatch?.payment_slips.map((ps) => (
                      <div key={ps.id}>{ps.payment_slip_no}</div>
                    ))}
                  </div>

                  <label />
                  <label>{paymentSlipBatchStrings.totalAmount}</label>
                  <div>
                    {moneyStrings.formatYenFront(
                      r.sumBy(initialPaymentSlipBatch?.payment_slips ?? [], (ps) =>
                        new PaymentSlipHelper(ps).getTotalAmount(),
                      ),
                    )}
                  </div>

                  <IndexedLabel index={2} field={fields.payee.fields.id} required />
                  <div>
                    <TextInput
                      field={fields.payee.fields.id}
                      inputOptions={{
                        type: 'choose',
                        options: [
                          ...payees.map((payee) => ({
                            label: payee.name_en,
                            value: payee.id,
                          })),
                          {
                            label: paymentSlipStrings.otherPayee,
                            value: 0,
                          },
                        ],
                      }}
                    />
                  </div>

                  <IndexedLabel field={fields.payee.fields.fc_no} />
                  <div>
                    <TextInput field={fields.payee.fields.fc_no} disabled />
                  </div>

                  <IndexedLabel field={fields.payee.fields.name_ja} required={isOtherPayee} />
                  <div>
                    <TextInput field={fields.payee.fields.name_ja} disabled={!isOtherPayee} />
                  </div>

                  <IndexedLabel field={fields.payee.fields.principal_name} />
                  <div>
                    <TextInput
                      field={fields.payee.fields.principal_name}
                      disabled={!isOtherPayee}
                    />
                  </div>

                  <IndexedLabel field={fields.payee.fields.phone} />
                  <div>
                    <TextInput field={fields.payee.fields.phone} disabled={!isOtherPayee} />
                  </div>

                  <IndexedLabel field={fields.payee.fields.email} />
                  <div>
                    <TextInput field={fields.payee.fields.email} disabled={!isOtherPayee} />
                  </div>

                  <IndexedLabel index={3} field={fields.deadline} required />
                  <div>
                    <TextInput field={fields.deadline} />
                  </div>

                  <IndexedLabel index={4} field={fields.payment_date} required />
                  <div>
                    <TextInput field={fields.payment_date} />
                  </div>

                  <IndexedLabel index={5} field={fields.invoice_files} />
                  <div>
                    <FileArrayInput field={fields.invoice_files} />
                  </div>

                  <div className={classes.split}>
                    <div className={classes.title}>
                      {paymentSlipBatchStrings.belowIsForAccounting}
                    </div>
                  </div>

                  <IndexedLabel index={6} field={fields.withdraw_date} />
                  <div>
                    <TextInput
                      field={fields.withdraw_date}
                      disabled={type === 'create'}
                      inputProps={{ placeholder: paymentSlipBatchStrings.ableToInputAfterWithdraw }}
                    />
                  </div>

                  <IndexedLabel index={7} field={fields.withdraw_no} />
                  <div>
                    <TextInput
                      field={fields.withdraw_no}
                      disabled={type === 'create'}
                      inputProps={{ placeholder: paymentSlipBatchStrings.ableToInputAfterWithdraw }}
                    />
                  </div>

                  <IndexedLabel index={8} field={fields.withdraw_certificate_files} />
                  <div>
                    <FileArrayInput
                      field={fields.withdraw_certificate_files}
                      readOnly={type === 'create'}
                    />
                  </div>
                </IndexedFormContainer>
              </div>
              <ActionableDialogActions justifyContent="center">
                <Button
                  color="primary"
                  size="small"
                  shape="round"
                  type="button"
                  text={formStrings.prev}
                  style={{ width: 90 }}
                  onClick={onClose}
                />
                <Button
                  color="primary"
                  size="small"
                  shape="round"
                  type="submit"
                  text={type === 'create' ? formStrings.next : formStrings.save}
                  style={{ width: 90 }}
                />
                <DebugForm form={form} />
              </ActionableDialogActions>
            </form>
          )}
          {step === 1 && (
            <>
              <div className={classes.messageContainer}>
                {paymentSlipBatchStrings.batchCreated(
                  <a
                    href={
                      url('/admin/invoice/PaymentSlipBatchDetails') +
                      '?' +
                      new URLSearchParams({
                        id: response!.id.toString(),
                        batchNo: response!.batch_no,
                      }).toString()
                    }
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes.link}
                  >
                    {response?.batch_no}
                  </a>,
                )}
              </div>
              <ActionableDialogActions justifyContent="center">
                <Button
                  color="primary"
                  size="small"
                  shape="round"
                  type="button"
                  text={formStrings.ok}
                  style={{ width: 90 }}
                  onClick={() => {
                    onClose();
                    setStep(0);
                  }}
                />
              </ActionableDialogActions>
            </>
          )}
        </ActionableDialog>
      </>
    );
  },
);
